import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Card, Typography, Button } from '@mui/joy';
import { XCircle as Error, House as Home, ArrowLeft as ArrowBack } from '@phosphor-icons/react';

interface ErrorPageProps {
  code?: number;
  title?: string;
  message?: string;
}

export const ErrorPage: React.FC<ErrorPageProps> = ({
  code = 404,
  title,
  message
}) => {
  const navigate = useNavigate();

  const getDefaultContent = (code: number) => {
    switch (code) {
      case 403:
        return {
          title: 'Access Forbidden',
          message: 'You don\'t have permission to access this resource.'
        };
      case 404:
        return {
          title: 'Page Not Found',
          message: 'The page you\'re looking for doesn\'t exist or has been moved.'
        };
      case 500:
        return {
          title: 'Server Error',
          message: 'Something went wrong on our end. Please try again later.'
        };
      default:
        return {
          title: 'Something Went Wrong',
          message: 'An unexpected error occurred.'
        };
    }
  };

  const defaultContent = getDefaultContent(code);
  const displayTitle = title || defaultContent.title;
  const displayMessage = message || defaultContent.message;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%', textAlign: 'center', p: 4 }}>
        <Error
          size={80}
          color="var(--joy-palette-danger-500)"
          style={{ marginBottom: 16 }}
        />
        
        <Typography 
          level="h1" 
          sx={{ 
            fontSize: '4rem', 
            fontWeight: 'bold',
            color: 'text.primary',
            mb: 1 
          }}
        >
          {code}
        </Typography>
        
        <Typography 
          level="h2" 
          sx={{ 
            fontSize: '1.5rem',
            fontWeight: 500,
            mb: 2 
          }}
        >
          {displayTitle}
        </Typography>
        
        <Typography 
          level="body-md" 
          sx={{ 
            color: 'text.secondary',
            mb: 4,
            lineHeight: 1.6 
          }}
        >
          {displayMessage}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            onClick={() => navigate(-1)}
            variant="outlined"
            startDecorator={<ArrowBack />}
          >
            Go Back
          </Button>
          
          <Button
            component={Link}
            to="/dashboard"
            variant="solid"
            startDecorator={<Home />}
            sx={{
              background: 'linear-gradient(135deg, #7013f5 0%, #5a0fc8 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a0fc8 0%, #4a0da8 100%)',
              }
            }}
          >
            Go Home
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

// Specific error page components
export const NotFoundPage: React.FC = () => (
  <ErrorPage code={404} />
);

export const ForbiddenPage: React.FC = () => (
  <ErrorPage code={403} />
);

export const ServerErrorPage: React.FC = () => (
  <ErrorPage code={500} />
);